import { useApi, useBearer } from '@healthblocks-io/core'
import Axios from 'axios'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useDebounce } from 'usehooks-ts'

import type { AllergySearchResult } from '@/modules/app/types/HealthParameter.types'

const MINIMUM_CHARACTERS = 3

export function useAllergySearch(query: string): {
  results: AllergySearchResult[]
  isLoading: boolean
} {
  const [results, setResults] = useState<AllergySearchResult[]>([])
  const [isFetching, setIsFetching] = useState(false)

  const debouncedQuery = useDebounce(query, 300)
  const baseURL = useApi()
  const bearer = useBearer()
  const axios = useMemo(() => Axios.create({ baseURL }), [baseURL])

  const requestIdRef = useRef(0)

  useEffect(() => {
    if (debouncedQuery.trim().length < MINIMUM_CHARACTERS) {
      requestIdRef.current++
      setResults([])
      setIsFetching(false)
      return
    }

    const requestId = ++requestIdRef.current
    setIsFetching(true)

    const doSearch = async () => {
      const bearerHeaders = await bearer()
      const response = await axios.get<AllergySearchResult[]>('/views/patient/allergy-code/search', {
        params: { q: debouncedQuery.trim() },
        headers: { ...bearerHeaders },
      })
      if (requestId === requestIdRef.current) {
        setResults(response.data)
        setIsFetching(false)
      }
    }

    doSearch().catch(() => {
      if (requestId === requestIdRef.current) {
        setResults([])
        setIsFetching(false)
      }
    })
  }, [debouncedQuery, axios, bearer])

  const isLoading = isFetching || (query.trim().length >= MINIMUM_CHARACTERS && query !== debouncedQuery)

  return { results, isLoading }
}
